import { JsonResponseData } from "csi/comm/JsonResponse";
import { FMT_AC_RIGHTS } from "csi/constants";
import csiStore from "csi/store/_base";
import Names, { StoreNames } from "csi/store/Names";
import { isDefined } from "csi/utility/coreUtility";
import { REQUEST_MODULE, REQUEST_OPERATION, createFormData, postFormData } from "src/c8-platform/core/request";
import { IAggregateStoreParams } from "src/c8-platform/type/aggregateStore";
import { FormFieldKeyMap, IForm, IFormInitialState } from "src/c8-platform/type/form";
import { cnl } from "src/c8-platform/type/value";
import { FormSaveResponse } from "../type/formResponse";
import { C8NodeType } from "../type/node";

export interface IGetForm extends Omit<IForm, "nodes" | "fields"> { }

interface JavaSearchResultAttributeData {
	// TODO: Figure out why a SearchResult returned from the Server sends $ as the Value if not Value.
	// If Value is not present, then the Value is the $
	$: string;
	// The property key
	Id: string;
	// The property value, if not present then the value is the $
	Value?: string;
}

/** utility function to create the names store from attribute id, node type, node url, and report operations */
export function createNamesStore(
	attributeId: string,
	nodeType: string,
	nodeUrl: string,
	reportOperations: string,
	emptyValue?: string,
): StoreNames {
	// TODO: add type for node store options
	const storeOptions: Record<string, any> = {};
	const aggregateStoreParams: IAggregateStoreParams = {
		attributeId,
		// TODO: add locale from user settings
		locale: "en",
		nodeType,
		sourceUrl: nodeUrl,
		reportOperations,
	};

	// Add aggregate store params to store options
	storeOptions.aggregateStoreParams = aggregateStoreParams;
	storeOptions.request = csiStore.getAggregateStoreRequest(aggregateStoreParams);

	// If a manually assigned empty value is provided, use that in the store otherwise Names.js will default to "centric:"
	if (isDefined(emptyValue)) {
		storeOptions.emptyValue = emptyValue;
	}

	// Create store
	const store = new Names(storeOptions);

	// Add callback when response is ready to store with field
	store.onAggregateStoreResponse = (res): void => {
		store.aggregateStoreResponse = res;
	};

	return store;
}

/** get domain form info for node type */
export async function getDomainFormInfoForNodeType(nodeType: C8NodeType): Promise<FormFieldKeyMap> {
	const formData = createFormData({
		[REQUEST_MODULE]: "Form",
		[REQUEST_OPERATION]: "GetDomainFormInfoForNodeType",
		FormType: nodeType,
	});

	let response = null;
	try {
		response = await postFormData<FormFieldKeyMap>(formData);
	} catch (e) {
		console.error(`Error getting Domain Form Info for nodeType '${nodeType}' with error`, e);
	}

	return response;
}

/** Get Form State by Node Type */
export async function getFormStateByNodeType(form: IGetForm): Promise<IForm | undefined> {
	const formInitialState: IFormInitialState = {
		disabledFields: form.disabledFields,
		initialValueMap: form.initialValueMap,
	};

	const formData = createFormData({
		[REQUEST_MODULE]: "Form",
		[REQUEST_OPERATION]: "GetFormState",
		NodeType: form.nodeType,
		NodeURL: form.nodeUrl,
		ParentURL: form.parentUrl,
		ParentAttributeId: form.parentAttributeId,
		SourceNodeURL: form.sourceNodeUrl,
		ContextURL: form.contextUrl,
		ContextAttributeId: form.contextAttributeId,
		FormInitialStateJSON: JSON.stringify(formInitialState),
	});

	let formInfoResponse = null;
	try {
		formInfoResponse = await postFormData<IForm>(formData);
	} catch (e) {
		console.error(`Error getting Form State for nodeType '${form.nodeType}' with error`, e);
	}

	return formInfoResponse;
}

/** set isNewFormBuilder attribute for FormDefinitionConfig */
export async function setIsNewFormBuilder(formDefinitionConfigURL: cnl, isNewFormBuilder: boolean): Promise<void> {
	const formData = createFormData({
		[REQUEST_MODULE]: "Form",
		[REQUEST_OPERATION]: "SetIsNewFormBuilder",
		URL: formDefinitionConfigURL,
		IsNewFormBuilder: isNewFormBuilder,
	});

	try {
		await postFormData<JsonResponseData>(formData);
	} catch (err) {
		console.error(`Can't set isNewFormBuilder attribute for FormDefinitionConfig - ${err.message}`);
	}
}

/** Update the form state based on a change to the field */
export async function updateFormState(form: IForm, fieldId: string): Promise<IForm | undefined> {
	const formData = createFormData({
		[REQUEST_MODULE]: "Form",
		[REQUEST_OPERATION]: "UpdateForm",
		NodeType: form.nodeType,
		NodeURL: form.nodeUrl,
		ParentURL: form.parentUrl,
		ParentAttributeId: form.parentAttributeId,
		SourceNodeURL: form.sourceNodeUrl,
		ContextURL: form.contextUrl,
		ContextAttributeId: form.contextAttributeId,
		FormFieldId: fieldId,
		FormStateJSON: JSON.stringify(form),
	});

	let formInfoResponse;
	try {
		formInfoResponse = await postFormData<JsonResponseData>(formData);
	} catch (err) {
		console.error(`Can't update the form - ${err.message}`);
	}
	return formInfoResponse;
}

/** validate and save the form */
export async function validateAndSaveFormState(form: IForm): Promise<FormSaveResponse | null> {
	const formData = createFormData({
		[REQUEST_MODULE]: "Form",
		[REQUEST_OPERATION]: "ValidateAndSaveForm",
		[FMT_AC_RIGHTS]: "Current",
		FormStateJSON: JSON.stringify(form),
	});

	try {
		const data = await postFormData<JsonResponseData>(formData);

		const jsonResponseAttributeData = data.ATTRIBUTES.Attribute as JavaSearchResultAttributeData[];
		let formInfo: IForm | null = null;
		let errorMessage = "";
		for (const attr of jsonResponseAttributeData) {
			const value = attr.Value || attr.$ || "";
			if (attr.Id === "formInfoJSON") {
				formInfo = JSON.parse(value) as IForm;
			}
			if (attr.Id === "validationErrorMessage") {
				errorMessage = value;
			}
		}

		const formSaveResponse: FormSaveResponse = {
			errorMessage,
			formInfo,
			formNode: data.NODES.ResultNode?.[0],
			supportNodes: data.NODES.Node,
		};

		return formSaveResponse;
	} catch (err) {
		console.error(`Can't validate and save the form -  ${err.message}`);
		return null;
	}
}

/** return true if node is being copied on form, which is the case if the node in the form does not match the source, but there is a source */
export function isCopyForm(formInfo: IForm): boolean {
	const { nodeUrl, sourceNodeUrl } = formInfo;
	return sourceNodeUrl && (nodeUrl !== sourceNodeUrl);
}

/** return true if node is being edited on form, which is the case if the node in the form matches the source */
export function isEditForm(formInfo: IForm): boolean {
	const { nodeUrl, sourceNodeUrl } = formInfo;
	return nodeUrl === sourceNodeUrl;
}

/** return true if node is being created on form, which is the case if there is no source */
export function isNewForm(formInfo: IForm): boolean {
	return !formInfo.sourceNodeUrl;
}
